import React, { useState, useCallback } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import { JiraProductDiscoveryLogo } from '@atlaskit/logo';
import { DocumentTitle } from '@atlassian/jira-global-document-title';
import { useIntl } from '@atlassian/jira-intl';
import FeedbackModal from '@atlassian/jira-polaris-lib-feedback';
import { useNotifications } from '@atlassian/jira-polaris-lib-notifications';
import { SCREEN, ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { Link, useQueryParam } from '@atlassian/jira-router';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import betaImage from './assets/heros.svg';
import image from './assets/magnifying_glass_error.svg';
import messages from './messages';

const COMMUNITY =
	'https://community.atlassian.com/t5/Jira-Product-Discovery-articles/How-to-move-from-beta-to-the-Free-or-Standard-plans-of-Jira/ba-p/2355268';
const PLAN = '/admin/billing-preview?referrerName=JIRA';

export const EndOfBetaPageAdmin = () => {
	const { formatMessage } = useIntl();
	const { success, error } = useNotifications();
	const [isOpen, setIsOpen] = useState(false);

	const onOpenDialog = useCallback(() => {
		setIsOpen(true);
	}, [setIsOpen]);

	const onCloseDialog = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);

	return (
		<>
			<DocumentTitle title={formatMessage(messages.pageTitle)} />
			<LogoContainer>
				<JiraProductDiscoveryLogo size="xlarge" appearance="brand" />
			</LogoContainer>
			<EmptyState
				width="wide"
				imageUrl={betaImage}
				imageWidth={210}
				header={formatMessage(messages.betaHeader)}
				description={formatMessage(messages.betaDescription)}
				primaryAction={
					<Button href={PLAN} target="_blank" appearance="primary">
						{formatMessage(messages.managePlan)}
					</Button>
				}
				secondaryAction={
					<Button onClick={onOpenDialog}>{formatMessage(messages.contactSupport)}</Button>
				}
				tertiaryAction={
					<Button href={COMMUNITY} appearance="link" target="_blank">
						{formatMessage(messages.learnMore)}
					</Button>
				}
			/>
			{isOpen && (
				<FeedbackModal
					onCancel={onCloseDialog}
					defaultCategoryName="help"
					onSuccess={success}
					onError={error}
				/>
			)}
		</>
	);
};

export const EndOfBetaPageNonAdmin = () => {
	const { formatMessage } = useIntl();

	return (
		<>
			<DocumentTitle title={formatMessage(messages.pageTitle)} />
			<LogoContainer>
				<JiraProductDiscoveryLogo size="xlarge" appearance="brand" />
			</LogoContainer>
			<EmptyState
				width="wide"
				imageUrl={betaImage}
				imageWidth={210}
				header={formatMessage(messages.betaHeader)}
				description={formatMessage(messages.betaDescriptionNonAdmin)}
				tertiaryAction={
					<Button href={COMMUNITY} appearance="link" target="_blank">
						{formatMessage(messages.learnMore)}
					</Button>
				}
			/>
		</>
	);
};

const EndOfBetaPage = () => {
	const { isSiteAdmin } = useTenantContext();
	if (isSiteAdmin) {
		return <EndOfBetaPageAdmin />;
	}
	return <EndOfBetaPageNonAdmin />;
};

export const UnlicensedPage = () => {
	const { formatMessage } = useIntl();

	return (
		<>
			<DocumentTitle title={formatMessage(messages.pageTitle)} />
			<EmptyState
				width="wide"
				imageUrl={image}
				header={formatMessage(messages.header)}
				description={formatMessage(messages.description)}
				primaryAction={<Link to="/jira/projects">{formatMessage(messages.action)}</Link>}
			/>
		</>
	);
};

export const LicenseFail = () => {
	const [beta] = useQueryParam('beta');

	if (beta) {
		return (
			<ContextualAnalyticsData sourceName="polarisEndOfBetaScreen" sourceType={SCREEN}>
				<EndOfBetaPage />
			</ContextualAnalyticsData>
		);
	}
	return (
		<ContextualAnalyticsData sourceName="polarisUnlicensedScreen" sourceType={SCREEN}>
			<UnlicensedPage />
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogoContainer = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '96px',
	textAlign: 'center',
});
